import React, { useEffect, useRef, useMemo } from 'react';
import * as Icons from '../../assets/svg/icons';

// Polished Google Meet / Zoom style color gradients
const AVATAR_GRADIENTS = [
  'from-blue-600 to-blue-800',
  'from-indigo-600 to-indigo-800',
  'from-emerald-600 to-emerald-800',
  'from-violet-600 to-violet-800',
  'from-rose-600 to-rose-800',
  'from-amber-600 to-amber-800',
  'from-teal-600 to-teal-700'
];

const ParticipantTile = ({ 
  participant, 
  isLarge = false, 
  className = '',
  onPointerDown,
  onResizePointerDown,
  stream,
  style,
  showLiveSession = false,
  showYouLabel = true
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    } else if (videoRef.current && !stream) {
      // Clear video if stream is removed
      videoRef.current.srcObject = null;
    }
  }, [stream, participant.isVideoOn]);

  // Strip "(Driver)" or any other bracketed roles from the name
  const cleanDisplayName = useMemo(() => {
    return participant.name.replace(/\([^)]*\)/g, '').trim();
  }, [participant.name]);

  // Generate initials from the cleaned name
  const initials = useMemo(() => {
    const parts = cleanDisplayName.split(/\s+/).filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [cleanDisplayName]);

  // Consistent gradient based on name hash
  const avatarGradient = useMemo(() => {
    const charCodeSum = participant.name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return AVATAR_GRADIENTS[charCodeSum % AVATAR_GRADIENTS.length];
  }, [participant.name]);

  const gradientMap = {
    'from-blue-600 to-blue-800': ['#2563eb', '#1e40af'],
    'from-indigo-600 to-indigo-800': ['#4f46e5', '#3730a3'],
    'from-emerald-600 to-emerald-800': ['#059669', '#065f46'],
    'from-violet-600 to-violet-800': ['#7c3aed', '#5b21b6'],
    'from-rose-600 to-rose-800': ['#e11d48', '#9f1239'],
    'from-amber-600 to-amber-800': ['#d97706', '#92400e'],
    'from-teal-600 to-teal-700': ['#0d9488', '#0f766e']
  };

  const gradientColors = gradientMap[avatarGradient] || ['#2563eb', '#1e40af'];

  return (
    <div 
      onPointerDown={onPointerDown}
      className={`relative overflow-hidden ${isLarge ? 'w-full h-full' : 'select-none touch-none'} ${className}`}
      style={{
        ...style,
        backgroundColor: isLarge ? 'transparent' : '#1f1f24',
        ...(isLarge ? {} : {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        })
      }}
    >
      {/* Video / Avatar Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {stream ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={participant.isLocal}
            className={`w-full h-full object-cover pointer-events-none ${participant.isLocal ? 'scale-x-[-1]' : ''}`}
          />
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full">
            {/* Meet/Zoom Styled Avatar Circle */}
            <div 
              className="rounded-full flex items-center justify-center transform transition-all duration-700"
              style={{
                background: (isLarge || participant.isLocal) ? 'linear-gradient(180deg, #6b7280 0%, #4b5563 100%)' : `linear-gradient(to bottom right, ${gradientColors[0]}, ${gradientColors[1]})`,
                color: (isLarge || participant.isLocal) ? '#e5e7eb' : '#fff',
                boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                width: isLarge ? '160px' : '56px',
                height: isLarge ? '160px' : '56px',
                fontSize: isLarge ? '4rem' : '1.25rem',
                transform: !participant.isVideoOn ? 'scale(0.9)' : 'scale(1)',
                opacity: !participant.isVideoOn ? 0.4 : 1,
                filter: !participant.isVideoOn ? 'grayscale(10%)' : 'none'
              }}
            >
              {(isLarge || participant.isLocal) ? (
                <svg className={isLarge ? 'w-16 h-16 text-gray-400' : 'w-7 h-7 text-gray-400'} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              ) : (
                <span className="font-semibold tracking-tighter drop-shadow-2xl select-none">{initials}</span>
              )}
            </div>
            
            {/* Name and status for large view */}
            {isLarge && (
              <div className="absolute bottom-32 flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                <p className="text-white font-bold text-2xl tracking-tight drop-shadow-lg">
                  {cleanDisplayName}
                </p>
                <div className="flex items-center gap-2">
                  {showLiveSession && (
                    <span className="font-medium uppercase flex items-center gap-1.5" style={{ fontSize: '11px', letterSpacing: '0.12em', color: '#9ca3af' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      LIVE SESSION
                    </span>
                  )}
                  {!showLiveSession && !participant.isVideoOn && (
                    <span className="font-black uppercase" style={{ fontSize: '12px', letterSpacing: '0.2em', color: '#71717a' }}>
                      Video Off
                    </span>
                  )}
                  {!showLiveSession && participant.isVideoOn && !stream && (
                    <span className="font-black uppercase flex items-center gap-2 animate-pulse" style={{ fontSize: '12px', letterSpacing: '0.2em', color: 'rgba(96, 165, 250, 0.8)' }}>
                      <div className="w-1 h-1 bg-current rounded-full" />
                      Connecting...
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resize Handle for PiP */}
      {!isLarge && onResizePointerDown && (
        <div 
          onPointerDown={(e) => {
            e.stopPropagation();
            onResizePointerDown(e);
          }}
          className="absolute bottom-0 right-0 w-12 h-12 flex items-end justify-end p-2 cursor-nwse-resize z-50 group"
        >
          <div className="w-5 h-5 border-r-2 border-b-2 border-white/20 rounded-br-md group-hover:border-white transition-colors" />
        </div>
      )}

      {/* Mic Muted Indicator */}
      {!participant.isAudioOn && (
        <div 
          className="absolute top-4 right-4 p-2 rounded-full shadow-lg z-10"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transform: 'scale(0.9)'
          }}
        >
          <Icons.MicOffIcon className="w-3.5 h-3.5" color="white" />
        </div>
      )}

      {/* Bottom Identity Badge (hidden when YOU is shown outside tile) */}
      {!isLarge && showYouLabel && (
        <div 
          className="absolute bottom-4 left-4 px-3 py-1.5 rounded-xl shadow-lg flex items-center overflow-hidden"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            maxWidth: 'calc(100% - 32px)'
          }}
        >
          <span className="font-medium truncate leading-none" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', letterSpacing: '-0.025em' }}>
            {participant.isLocal ? 'You' : cleanDisplayName}
          </span>
        </div>
      )}
    </div>
  );
};

export default ParticipantTile;